import type { UTCDate } from '../../utils/fechador'
import * as dt from '../../utils/fechador'
import * as fechador from '../../utils/fechador'
import { log } from '../../utils/logger'
import * as storage from '../../utils/storage'
import { STORAGE_KEYS } from '../modelo/storageKeys'

export interface FetchOptions {
    debug: boolean
}

// helper for graphics: labels -> fechas, datasets -> valores
export type TimeSeries = { fechas: UTCDate[]; valores: number[] }

// BOE-code added to the TimeSeries
export type SerieData = { serie: string } & TimeSeries

// IRS-tenor added to de SerieData
export type PilarData = { tenor: number } & SerieData

const API_CODES: Record<string, string> = {
    '1y': 'D_1T9VM001',
    '2y': 'D_1T9VM002',
    '3y': 'D_1T9VM003',
    '4y': 'D_1T9VM004',
    '5y': 'D_1T9H0011', //D_1T9VM005' no existe
    '7y': 'D_1T9VM006',
    '10y': 'D_1T9VM007',
    '15y': 'D_1T9VM008',
    '20y': 'D_1T9VM009',
    '30y': 'D_1T9VM010'
} as const satisfies Record<string, string>
type Tenor = keyof typeof API_CODES

function getTenorFromCode(code: (typeof API_CODES)[Tenor]): number {
    const entry = Object.entries(API_CODES).find(([, value]) => value === code)!
    return parseInt(entry[0])
}

// data => dates as string
function mapToPilars(data: any): PilarData[] {
    if (!Array.isArray(data)) return []
    return data.map((item) => ({
        tenor: getTenorFromCode(item.serie),
        serie: item.serie,
        fechas: item.fechas.map((f: string) => fechador.fromString(f)),
        valores: item.valores
    }))
}

// https://app.bde.es/bierest/resources/srdatosapp/favoritas?idioma=es&series=D_1T9VM001,D_1T9VM002,D_1T9VM003,D_1T9VM004,D_1T9H0011,D_1T9VM006,D_1T9VM007,D_1T9VM008,D_1T9VM009,D_1T9VM010
// const UrlIRS =
//     'https://app.bde.es/bierest/resources/srdatosapp/listaSeries?idioma=es&series=D_1T9VM001,D_1T9VM002,D_1T9VM003,D_1T9VM004,D_1T9H0011,D_1T9VM006,D_1T9VM007,D_1T9VM008,D_1T9VM009,D_1T9VM010&rango=30M'
const UrlIRS = 'https://mercado.juan-turbera.workers.dev/api/irs'

// --- 1. Cache lookup -------------------------------------------------
interface CacheHit {
    pilars: PilarData[]
    storageDate: UTCDate
    effectiveDate: UTCDate
}

function readIRSCache(): CacheHit | null {
    const parsed = storage.getParsed(STORAGE_KEYS.irs)
    if (!parsed) return null

    try {
        const storageDate = dt.fromString(parsed.date)
        const pilars = mapToPilars(parsed.data)
        return { pilars, storageDate, effectiveDate: pilars[0].fechas[0] }
    } catch (err) {
        log.error('IRS desde localStorage fallado (fetch de nuevo):', err)
        return null
    }
}

function isCacheStillValid(hit: CacheHit): boolean {
    return (
        fechador.diffMonths(hit.effectiveDate, fechador.firstNext()) <= 2 ||
        fechador.eqDay(hit.storageDate, fechador.todayUTC())
    )
}

// --- 2. Network / debug source ---------------------------------------
async function loadIRSSource(options: FetchOptions): Promise<any[] | null> {
    if (options.debug) return irsRaw

    try {
        const res = await fetch(UrlIRS, { signal: AbortSignal.timeout(3000) })

        if (!res.ok) {
            log.warn(`Not OK, fetch IRS: status ${res.status}`)
            return null
        }

        const jsonArray = await res.json()
        log.info('IRS fetched del API con éxito.')
        return jsonArray
    } catch (err: any) {
        if (err.name === 'TimeoutError') {
            log.error('Fetch IRS cancelado: La API tardó más de 3 segundos en responder.')
        } else if (err instanceof SyntaxError) {
            log.error('JSON IRS inválido:', err)
        } else {
            log.error('Fetch IRS falló por error de red o de sistema:', err)
        }
        return null
    }
}

// --- 3. Mapping (already isolated, just typed) ------------------------
function safeMapToPilars(jsonArray: any[]): PilarData[] | null {
    try {
        return mapToPilars(jsonArray)
    } catch (err) {
        log.error('IRS mapping fallado:', err)
        return null
    }
}

// --- 4. Persist + log --------------------------------------------------
function persistIRS(pilars: PilarData[], options: FetchOptions): void {
    storage.setJson(STORAGE_KEYS.irs, pilars)

    const mode = options.debug ? 'DEBUG' : 'LIFE'
    const effectiveDate = pilars[0].fechas[0]
    const label = options.debug ? 'RAW_FAKE' : 'FETCH_BDE'
    const level = options.debug ? log.debug : log.info
    level(`IRS ${mode} ${label} efectivo:${dt.toYMD(effectiveDate)}`)
}

// --- Orchestrator --------------------------------------------------------
export async function fetchIRS(options: FetchOptions): Promise<PilarData[]> {
    const mode = options.debug ? 'DEBUG' : 'LIFE'

    const cached = readIRSCache()
    if (cached && isCacheStillValid(cached)) {
        log.debug(
            `IRS ${mode} localStorage:${dt.toYMD(cached.storageDate)} efectivo:${dt.toYMD(cached.effectiveDate)}`
        )
        return cached.pilars
    }

    const jsonArray = await loadIRSSource(options)
    if (!jsonArray) return []

    const pilars = safeMapToPilars(jsonArray)
    if (!pilars) return []

    persistIRS(pilars, options)
    return pilars
}

//     // return array of pilars (tenor/serie with TimeSeries: fechas/valores)
// export async function fetchIRS(options: FetchOptions): Promise<PilarData[]> {
//     const mode = options.debug ? 'DEBUG' : 'LIFE'
//     // 1. check localStorage
//     const parsed = storage.getParsed(STORAGE_KEYS.irs)
//     if (parsed) {
//         try {
//             const storageDate = dt.fromString(parsed.date)
//             const pilars: PilarData[] = mapToPilars(parsed.data)
//             const effectiveDate = pilars[0].fechas[0]

//             if (fechador.diffMonths(pilars[0].fechas[0], fechador.firstNext()) <= 2) {
//                 log.debug(
//                     `IRS ${mode} localStorage:${dt.toYMD(storageDate)} efectivo:${dt.toYMD(effectiveDate)}`
//                 )
//                 return pilars
//             }
//             // const storageDate = fechador.fromString(parsed.date)
//             if (fechador.eqDay(storageDate, fechador.todayUTC())) {
//                 log.debug(`IRS ${mode} localStorage TODAY efectivo:${dt.toYMD(effectiveDate)}`)
//                 return pilars
//             }
//         } catch (err) {
//             // si está corrupto, ignoramos y hacemos fetch
//             log.error('IRS desde localStorage fallado (fetch de nuevo):', err)
//         }
//     }

//     // 2. fetch desde servidor (solo errores de red)
//     let jsonArray: any[]
//     try {
//         if (options.debug) {
//             jsonArray = irsRaw
//         } else {
//             const res = await fetch(UrlIRS, {
//                 signal: AbortSignal.timeout(3000)
//             })

//             if (!res.ok) {
//                 log.warn(`Not OK, fetch IRS: status ${res.status}`)
//                 return []
//             }

//             try {
//                 jsonArray = await res.json()
//                 log.info('IRS fetched del API con éxito.')
//             } catch (err) {
//                 log.error('JSON IRS inválido:', err)
//                 return []
//             }
//         }
//     } catch (err: any) {
//         // log.error('Fetch IRS fallado:', err)
//         // OUTER CATCH: Distinguir Timeout de Error de Red
//         if (err.name === 'TimeoutError') {
//             log.error('Fetch IRS cancelado: La API tardó más de 3 segundos en responder.')
//         } else {
//             log.error('Fetch IRS falló por error de red o de sistema:', err)
//         }
//         return []
//     }

//     // 3. map (aislado)
//     let pilars: PilarData[]
//     try {
//         pilars = mapToPilars(jsonArray)
//     } catch (err) {
//         log.error('IRS mapping fallado:', err)
//         return []
//     }

//     storage.setJson(STORAGE_KEYS.irs, pilars)

//     const effectiveDate = pilars[0].fechas[0]
//     if (options.debug) log.debug(`IRS ${mode} RAW_FAKE efectivo:${dt.toYMD(effectiveDate)}`)
//     else log.info(`IRS ${mode} FETCH_BDE efectivo:${dt.toYMD(effectiveDate)}`)

//     return pilars
// }

// ****************************************************************************
// test-data ******************************************************************
// ****************************************************************************
const irsRaw = [
    {
        serie: 'D_1T9VM001',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A UN AÑO',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A un año',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A un año'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A UN AÑO'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,223 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 2,814 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Ene 2021] -0,558 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,991 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            2.814, 2.718, 2.633, 2.629, 2.571, 2.434, 2.056, 2.078, 2.094, 2.045, 2.016, 2.015,
            1.971, 1.907, 1.896, 1.908, 1.973, 2.242, 2.236, 2.378, 2.276, 2.276, 2.581, 2.821,
            3.038, 3.394, 3.526, 3.581, 3.563, 3.573, 3.545
        ]
    },
    {
        serie: 'D_1T9VM002',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DOS AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A dos años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A dos años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DOS AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,480 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,012 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,530 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Jul 2023] 3,835 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.012, 2.909, 2.798, 2.834, 2.76, 2.626, 2.206, 2.246, 2.263, 2.162, 2.113, 2.124,
            2.079, 2.019, 1.985, 1.984, 1.983, 2.269, 2.22, 2.349, 2.154, 2.263, 2.357, 2.476,
            2.709, 3.097, 3.258, 3.326, 3.254, 3.178, 3.123
        ]
    },
    {
        serie: 'D_1T9VM003',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TRES AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A tres años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A tres años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TRES AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,602 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,032 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,518 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Jul 2023] 3,583 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.032, 2.921, 2.791, 2.841, 2.779, 2.663, 2.291, 2.353, 2.369, 2.237, 2.181, 2.202,
            2.167, 2.117, 2.065, 2.067, 2.056, 2.33, 2.228, 2.354, 2.121, 2.237, 2.297, 2.367,
            2.561, 2.92, 3.065, 3.117, 3.037, 2.933, 2.897
        ]
    },
    {
        serie: 'D_1T9VM004',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A CUATRO AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A cuatro años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A cuatro años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A CUATRO AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,772 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,047 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,498 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,432 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.047, 2.936, 2.801, 2.859, 2.807, 2.701, 2.378, 2.453, 2.464, 2.317, 2.257, 2.28,
            2.256, 2.214, 2.152, 2.156, 2.144, 2.394, 2.251, 2.373, 2.125, 2.242, 2.293, 2.338,
            2.499, 2.828, 2.949, 2.984, 2.906, 2.8, 2.788
        ]
    },
    {
        serie: 'D_1T9H0011',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA MERCADO HIPOTECARIO (Orden EHA/2899/2011 y CBE 5/12) INTEREST RATE SWAP A 5 AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Oficiales. Mercado hipotecario. IRS a 5 años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Oficiales. Mercado hipotecario. IRS a 5 años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA MERCADO HIPOTECARIO (Orden EHA/2899/2011 y CBE 5/12) INTEREST RATE SWAP A 5 AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '210'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Mar 2009] 0,090 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,067 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,469 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,386 %'
            }
        ],
        fechaInicio: '2009-03-01T09:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.067, 2.957, 2.823, 2.886, 2.842, 2.741, 2.458, 2.54, 2.548, 2.393, 2.328, 2.355,
            2.336, 2.301, 2.233, 2.237, 2.225, 2.451, 2.273, 2.391, 2.136, 2.251, 2.304, 2.336,
            2.471, 2.777, 2.876, 2.898, 2.827, 2.722, 2.734
        ]
    },
    {
        serie: 'D_1T9VM006',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A SIETE AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A siete años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A siete años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A SIETE AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 1,358 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,124 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,396 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,369 %'
            },
            {
                titulo: 'Notas',
                descripcion:
                    'La publicación en el BOE de los datos de diciembre de 2023 con fecha del 8 de enero de 2024 fue corregida mediante una fe de erratas publicada en el BOE de 9 de enero de 2024'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.124, 3.017, 2.891, 2.959, 2.926, 2.829, 2.606, 2.694, 2.698, 2.532, 2.46, 2.492,
            2.481, 2.456, 2.378, 2.38, 2.364, 2.547, 2.317, 2.43, 2.165, 2.279, 2.344, 2.363, 2.463,
            2.739, 2.809, 2.816, 2.759, 2.656, 2.7
        ]
    },
    {
        serie: 'D_1T9VM007',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DIEZ AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A diez años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A diez años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DIEZ AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 1,794 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,227 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,265 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,410 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.227, 3.124, 3.01, 3.081, 3.058, 2.962, 2.796, 2.885, 2.887, 2.713, 2.632, 2.671,
            2.664, 2.641, 2.55, 2.545, 2.521, 2.66, 2.384, 2.489, 2.211, 2.327, 2.415, 2.43, 2.501,
            2.751, 2.795, 2.795, 2.75, 2.646, 2.719
        ]
    },
    {
        serie: 'D_1T9VM008',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A QUINCE AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A quince años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A quince años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A QUINCE AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 2,222 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,361 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,080 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,462 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.361, 3.267, 3.17, 3.236, 3.219, 3.119, 3.017, 3.099, 3.102, 2.921, 2.825, 2.866,
            2.854, 2.823, 2.71, 2.688, 2.645, 2.761, 2.448, 2.544, 2.266, 2.374, 2.499, 2.511,
            2.558, 2.787, 2.806, 2.804, 2.768, 2.668, 2.762
        ]
    },
    {
        serie: 'D_1T9VM009',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A VEINTE AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A veinte años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A veinte años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A VEINTE AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 2,343 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,404 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] 0,000 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Ago 2026] 3,404 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.404, 3.315, 3.224, 3.275, 3.256, 3.153, 3.109, 3.186, 3.19, 3, 2.888, 2.929, 2.909,
            2.862, 2.73, 2.687, 2.628, 2.738, 2.406, 2.488, 2.212, 2.311, 2.464, 2.476, 2.503,
            2.722, 2.726, 2.727, 2.692, 2.597, 2.695
        ]
    },
    {
        serie: 'D_1T9VM010',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TREINTA AÑOS',
        descripcionCorta:
            'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A treinta años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipos de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A treinta años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TREINTA AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '167'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 2,387 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[Ago 2026] 3,331 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Oct 2020] -0,026 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Ago 2026] 3,331 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-08-01T08:15:00Z',
        fechas: [
            '2026-08-01T08:15:00Z',
            '2026-07-01T08:15:00Z',
            '2026-06-01T08:15:00Z',
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z'
        ],
        valores: [
            3.331, 3.248, 3.165, 3.198, 3.174, 3.07, 3.107, 3.181, 3.203, 3.006, 2.872, 2.915,
            2.882, 2.815, 2.656, 2.577, 2.488, 2.59, 2.225, 2.282, 2.011, 2.095, 2.286, 2.298, 2.3,
            2.505, 2.496, 2.502, 2.47, 2.382, 2.483
        ]
    }
]

const irsRaw_20260501 = [
    {
        serie: 'D_1T9VM001',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A UN AÑO',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A un año',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A un año'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A UN AÑO'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,223 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,629 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Ene 2021] -0,558 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,991 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            2.629, 2.571, 2.434, 2.056, 2.078, 2.094, 2.045, 2.016, 2.015, 1.971, 1.907, 1.896,
            1.908, 1.973, 2.242, 2.236, 2.378, 2.276, 2.276, 2.581, 2.821, 3.038, 3.394, 3.526,
            3.581, 3.563, 3.573, 3.545, 3.441, 3.489, 3.852
        ]
    },
    {
        serie: 'D_1T9VM002',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DOS AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A dos años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A dos años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DOS AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,480 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,834 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,530 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Jul 2023] 3,835 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            2.834, 2.76, 2.626, 2.206, 2.246, 2.263, 2.162, 2.113, 2.124, 2.079, 2.019, 1.985,
            1.984, 1.983, 2.269, 2.22, 2.349, 2.154, 2.263, 2.357, 2.476, 2.709, 3.097, 3.258,
            3.326, 3.254, 3.178, 3.123, 2.947, 2.995, 3.506
        ]
    },
    {
        serie: 'D_1T9VM003',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TRES AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A tres años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A tres años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TRES AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,602 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,841 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,518 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Jul 2023] 3,583 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            2.841, 2.779, 2.663, 2.291, 2.353, 2.369, 2.237, 2.181, 2.202, 2.167, 2.117, 2.065,
            2.067, 2.056, 2.33, 2.228, 2.354, 2.121, 2.237, 2.297, 2.367, 2.561, 2.92, 3.065, 3.117,
            3.037, 2.933, 2.897, 2.718, 2.743, 3.263
        ]
    },
    {
        serie: 'D_1T9VM004',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A CUATRO AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A cuatro años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A cuatro años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A CUATRO AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 0,772 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,859 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,498 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,432 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            2.859, 2.807, 2.701, 2.378, 2.453, 2.464, 2.317, 2.257, 2.28, 2.256, 2.214, 2.152,
            2.156, 2.144, 2.394, 2.251, 2.373, 2.125, 2.242, 2.293, 2.338, 2.499, 2.828, 2.949,
            2.984, 2.906, 2.8, 2.788, 2.625, 2.634, 3.15
        ]
    },
    {
        serie: 'D_1T9H0011',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA MERCADO HIPOTECARIO (Orden EHA/2899/2011 y CBE 5/12) INTEREST RATE SWAP A 5 AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Oficiales. Mercado hipotecario. interest Rate Swap a 5 años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Oficiales. Mercado hipotecario. interest Rate Swap a 5 años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA MERCADO HIPOTECARIO (Orden EHA/2899/2011 y CBE 5/12) INTEREST RATE SWAP A 5 AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '207'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Mar 2009] 0,090 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,886 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,469 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,386 %'
            }
        ],
        fechaInicio: '2009-03-01T09:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            2.886, 2.842, 2.741, 2.458, 2.54, 2.548, 2.393, 2.328, 2.355, 2.336, 2.301, 2.233,
            2.237, 2.225, 2.451, 2.273, 2.391, 2.136, 2.251, 2.304, 2.336, 2.471, 2.777, 2.876,
            2.898, 2.827, 2.722, 2.734, 2.591, 2.589, 3.102
        ]
    },
    {
        serie: 'D_1T9VM006',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A SIETE AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A siete años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A siete años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A SIETE AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 1,358 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 2,959 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,396 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,369 %'
            },
            {
                titulo: 'Notas',
                descripcion:
                    'La publicación en el BOE de los datos de diciembre de 2023 con fecha del 8 de enero de 2024 fue corregida mediante una fe de erratas publicada en el BOE de 9 de enero de 2024'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            2.959, 2.926, 2.829, 2.606, 2.694, 2.698, 2.532, 2.46, 2.492, 2.481, 2.456, 2.378, 2.38,
            2.364, 2.547, 2.317, 2.43, 2.165, 2.279, 2.344, 2.363, 2.463, 2.739, 2.809, 2.816,
            2.759, 2.656, 2.7, 2.587, 2.572, 3.08
        ]
    },
    {
        serie: 'D_1T9VM007',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DIEZ AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A diez años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A diez años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A DIEZ AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 1,794 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 3,081 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,265 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,410 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            3.081, 3.058, 2.962, 2.796, 2.885, 2.887, 2.713, 2.632, 2.671, 2.664, 2.641, 2.55,
            2.545, 2.521, 2.66, 2.384, 2.489, 2.211, 2.327, 2.415, 2.43, 2.501, 2.751, 2.795, 2.795,
            2.75, 2.646, 2.719, 2.633, 2.608, 3.119
        ]
    },
    {
        serie: 'D_1T9VM008',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A QUINCE AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A quince años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A quince años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A QUINCE AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 2,222 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 3,236 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] -0,080 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,462 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            3.236, 3.219, 3.119, 3.017, 3.099, 3.102, 2.921, 2.825, 2.866, 2.854, 2.823, 2.71,
            2.688, 2.645, 2.761, 2.448, 2.544, 2.266, 2.374, 2.499, 2.511, 2.558, 2.787, 2.806,
            2.804, 2.768, 2.668, 2.762, 2.697, 2.665, 3.18
        ]
    },
    {
        serie: 'D_1T9VM009',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A VEINTE AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A veinte años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A veinte años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A VEINTE AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 2,343 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 3,275 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Dic 2020] 0,000 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Oct 2023] 3,373 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            3.275, 3.256, 3.153, 3.109, 3.186, 3.19, 3, 2.888, 2.929, 2.909, 2.862, 2.73, 2.687,
            2.628, 2.738, 2.406, 2.488, 2.212, 2.311, 2.464, 2.476, 2.503, 2.722, 2.726, 2.727,
            2.692, 2.597, 2.695, 2.64, 2.606, 3.108
        ]
    },
    {
        serie: 'D_1T9VM010',
        descripcion:
            'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TREINTA AÑOS',
        descripcionCorta:
            'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A treinta años',
        codFrecuencia: 'M',
        decimales: 3,
        simbolo: '%',
        informacion: [
            {
                titulo: 'Nombre',
                descripcion:
                    'Tipo de interés. De referencia. Préstamos hipotecarios para calcular el valor de mercado. IRS. A treinta años'
            },
            {
                titulo: 'Descripción',
                descripcion:
                    'TIPOS DE INTERES DE REFERENCIA PRÉSTAMOS HIPOTECARIOS PARA EL CÁLCULO DEL VALOR DE MERCADO INTERES RATE SWAP (IRS) A TREINTA AÑOS'
            },
            {
                titulo: 'Unidades',
                descripcion: 'Porcentaje'
            },
            {
                titulo: 'Decimales',
                descripcion: '3'
            },
            {
                titulo: 'Número de observaciones',
                descripcion: '164'
            },
            {
                titulo: 'Primer valor',
                descripcion: '[Oct 2012] 2,387 %'
            },
            {
                titulo: 'Último valor',
                descripcion: '[May 2026] 3,198 %'
            },
            {
                titulo: 'Valor mínimo',
                descripcion: '[Oct 2020] -0,026 %'
            },
            {
                titulo: 'Valor máximo',
                descripcion: '[Dic 2025] 3,203 %'
            }
        ],
        fechaInicio: '2012-10-01T08:15:00Z',
        fechaFin: '2026-05-01T08:15:00Z',
        fechas: [
            '2026-05-01T08:15:00Z',
            '2026-04-01T08:15:00Z',
            '2026-03-01T09:15:00Z',
            '2026-02-01T09:15:00Z',
            '2026-01-01T09:15:00Z',
            '2025-12-01T09:15:00Z',
            '2025-11-01T09:15:00Z',
            '2025-10-01T08:15:00Z',
            '2025-09-01T08:15:00Z',
            '2025-08-01T08:15:00Z',
            '2025-07-01T08:15:00Z',
            '2025-06-01T08:15:00Z',
            '2025-05-01T08:15:00Z',
            '2025-04-01T08:15:00Z',
            '2025-03-01T09:15:00Z',
            '2025-02-01T09:15:00Z',
            '2025-01-01T09:15:00Z',
            '2024-12-01T09:15:00Z',
            '2024-11-01T09:15:00Z',
            '2024-10-01T08:15:00Z',
            '2024-09-01T08:15:00Z',
            '2024-08-01T08:15:00Z',
            '2024-07-01T08:15:00Z',
            '2024-06-01T08:15:00Z',
            '2024-05-01T08:15:00Z',
            '2024-04-01T08:15:00Z',
            '2024-03-01T09:15:00Z',
            '2024-02-01T09:15:00Z',
            '2024-01-01T09:15:00Z',
            '2023-12-01T09:15:00Z',
            '2023-11-01T09:15:00Z'
        ],
        valores: [
            3.198, 3.174, 3.07, 3.107, 3.181, 3.203, 3.006, 2.872, 2.915, 2.882, 2.815, 2.656,
            2.577, 2.488, 2.59, 2.225, 2.282, 2.011, 2.095, 2.286, 2.298, 2.3, 2.505, 2.496, 2.502,
            2.47, 2.382, 2.483, 2.445, 2.414, 2.88
        ]
    }
]
